import React, { useEffect, useMemo, useState } from "react";
import { PaystackButton } from "react-paystack";
import { useNavigate } from "react-router-dom";
import "./CSS/Checkout.css";
import { useCartStore } from "../stores/useCartStores";
import { useOrderStore } from "../stores/useOrderStores";
import axios from "../lib/axios.js";

const ZONE_PRICES = {
  1: 67988.65,
  2: 72732.04,
  3: 87865.74,
  4: 96449,
  5: 98256.02,
  6: 107065.14,
  7: 113841.45,
  8: 127845.76,
};

const COUNTRY_ZONES = {
  "United Kingdom": 1,
  Ireland: 1,
  Guernsey: 1,
  Jersey: 1,
  Benin: 2,
  "Burkina Faso": 2,
  Cameroon: 2,
  "Central African Republic": 2,
  Chad: 2,
  "Côte d'Ivoire": 2,
  Gabon: 2,
  Gambia: 2,
  Guinea: 2,
  "Equatorial Guinea": 2,
  "Guinea-Bissau": 2,
  Mali: 2,
  Niger: 2,
  Senegal: 2,
  "Sierra Leone": 2,
  Togo: 2,
  "São Tomé and Príncipe": 2,
  Liberia: 2,
  USA: 3,
  Canada: 3,
  Mexico: 3,
  France: 4,
  Germany: 4,
  Netherlands: 4,
  Italy: 4,
  Spain: 4,
  Sweden: 4,
  Norway: 4,
  Ukraine: 4,
  Turkey: 4,
  Russia: 4,
  "South Africa": 5,
  Morocco: 5,
  Egypt: 5,
  Libya: 5,
  Sudan: 5,
  Ethiopia: 5,
  Kenya: 5,
  Tanzania: 5,
  Uganda: 5,
  Zimbabwe: 5,
  Zambia: 5,
  Namibia: 5,
  Algeria: 5,
  UAE: 6,
  "Saudi Arabia": 6,
  Qatar: 6,
  Oman: 6,
  Jordan: 6,
  Lebanon: 6,
  Israel: 6,
  Iran: 6,
  Iraq: 6,
  Syria: 6,
  Yemen: 6,
  India: 7,
  China: 7,
  Japan: 7,
  Korea: 7,
  Indonesia: 7,
  Malaysia: 7,
  Singapore: 7,
  Thailand: 7,
  Vietnam: 7,
  Philippines: 7,
  Australia: 8,
  "New Zealand": 8,
  Jamaica: 8,
  Venezuela: 8,
  "Virgin Islands": 8,
  Samoa: 8,
  Tuvalu: 8,
};

const NIGERIAN_STATES = {
  FCT: 3500,
  Lagos: 10000,
  Kaduna: 8000,
  Kano: 8000,
  Abia: 0,
  Adamawa: 0,
  "Akwa Ibom": 0,
  Anambra: 0,
  Bauchi: 0,
  Bayelsa: 0,
  Benue: 0,
  Borno: 0,
  "Cross River": 0,
  Delta: 0,
  Ebonyi: 0,
  Edo: 0,
  Ekiti: 0,
  Enugu: 0,
  Gombe: 0,
  Imo: 0,
  Jigawa: 0,
  Katsina: 0,
  Kebbi: 0,
  Kogi: 0,
  Kwara: 0,
  Nasarawa: 0,
  Niger: 0,
  Ogun: 0,
  Ondo: 0,
  Osun: 0,
  Oyo: 0,
  Plateau: 0,
  Rivers: 0,
  Sokoto: 0,
  Taraba: 0,
  Yobe: 0,
  Zamfara: 0,
};

const COUNTRIES = ["Nigeria", ...Object.keys(COUNTRY_ZONES)];

const formatMoney = (value) => {
  const amount = Number(value);

  return (Number.isFinite(amount) ? amount : 0).toLocaleString(
    "en-NG",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const Checkout = () => {
  const { cartItems, getCartProducts } = useCartStore();
  const { createOrder } = useOrderStore();
  const navigate = useNavigate();

  const [floatingMessage, setFloatingMessage] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("Courier");
  const [stockRefreshing, setStockRefreshing] = useState(true);
  const [shippingAddress, setShippingAddress] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    telephone: "",
    measurement: "",
  });

  useEffect(() => {
    getCartProducts().finally(() => setStockRefreshing(false));
  }, [getCartProducts]);

  const showFloatingMessage = (message) => {
    setFloatingMessage(message);
    window.setTimeout(() => setFloatingMessage(""), 2500);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setShippingAddress((currentAddress) => ({
      ...currentAddress,
      [name]: value,
      ...(name === "country" ? { state: "" } : {}),
    }));

    if (
      name === "country" &&
      value !== "Nigeria" &&
      deliveryMethod === "CarPark"
    ) {
      setDeliveryMethod("Courier");
    }
  };

  const totalItemQuantity = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      ),
    [cartItems]
  );

  const cartTotal = useMemo(
    () =>
      cartItems.reduce(
        (total, item) =>
          total + Number(item.price || 0) * Number(item.quantity || 0),
        0
      ),
    [cartItems]
  );

  const baseShippingFee = useMemo(() => {
    if (shippingAddress.country === "Nigeria") {
      return Number(NIGERIAN_STATES[shippingAddress.state] || 0);
    }

    const zone = COUNTRY_ZONES[shippingAddress.country];
    return Number(ZONE_PRICES[zone] || 0);
  }, [shippingAddress.country, shippingAddress.state]);

  const shippingFee =
    deliveryMethod === "Courier"
      ? baseShippingFee * totalItemQuantity
      : 0;
  const totalAmount = cartTotal + shippingFee;
  const amountToPay = Math.round(totalAmount * 100);
  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "";

  const validateForm = () => {
    const requiredValues = [
      shippingAddress.name,
      shippingAddress.email,
      shippingAddress.address,
      shippingAddress.city,
      shippingAddress.country,
      shippingAddress.telephone,
    ];

    if (shippingAddress.country === "Nigeria") {
      requiredValues.push(shippingAddress.state);
    }

    return requiredValues.every(
      (value) => String(value || "").trim() !== ""
    );
  };

  const stockIssue = cartItems.find(
    (item) =>
      Number(item.available_quantity || 0) <= 0 ||
      Number(item.quantity) > Number(item.available_quantity || 0)
  );

  const paymentBlockReason = stockRefreshing
    ? "Checking current product availability."
    : !cartItems.length
    ? "Your shopping bag is empty."
    : stockIssue
    ? "Update the unavailable item in your bag before continuing."
    : !validateForm()
    ? "Complete all required delivery details to continue."
    : amountToPay <= 0
    ? "Your order total must be greater than zero."
    : !publicKey
    ? "Payment is not configured. Add the Paystack public key to the storefront environment file."
    : "";

  const canPay = !paymentBlockReason;

  const handlePaymentSuccess = async (response) => {
    const formattedAddress = [
      shippingAddress.address,
      shippingAddress.city,
      shippingAddress.state,
      shippingAddress.country,
      shippingAddress.zip,
    ]
      .filter(Boolean)
      .join(", ");

    const orderData = {
      products: cartItems.map(({ id, quantity, size }) => ({
        id,
        quantity,
        size,
      })),
      shipping_address: formattedAddress,
      shipping_fee: shippingFee,
      total_price: totalAmount,
      reference: response.reference || response.trxref,
      measurement: shippingAddress.measurement,
      name: shippingAddress.name,
      email: shippingAddress.email,
      telephone: shippingAddress.telephone,
      order_date: new Date().toISOString(),
    };

    try {
      const orderResponse = await createOrder(
        orderData.products,
        orderData.shipping_address,
        orderData.total_price,
        orderData.reference,
        orderData.measurement,
        orderData.name,
        orderData.email,
        orderData.telephone,
        orderData.order_date,
        {
          shipping_country: shippingAddress.country,
          shipping_state: shippingAddress.state,
          delivery_method: deliveryMethod,
        }
      );

      if (!orderResponse.duplicate) {
        axios.post("/send-email/order", orderData).catch((emailError) => {
          console.error("Order email failed:", emailError);
        });
      }

      showFloatingMessage("Order created successfully");
      window.setTimeout(() => navigate("/orders"), 1000);
    } catch (error) {
      showFloatingMessage(
        error.response?.data?.message ||
          "The order could not be completed. Please contact Posher."
      );
    }
  };

  const handlePaymentClose = () => {
    showFloatingMessage("Payment process cancelled.");
  };

  return (
    <div className="checkout">
      <div className="billing-address">
        <h2>BILLING ADDRESS</h2>
        <form>
          <label>NAME</label>
          <input type="text" name="name" value={shippingAddress.name} onChange={handleInputChange} required />

          <label>EMAIL</label>
          <input type="email" name="email" value={shippingAddress.email} onChange={handleInputChange} required />

          <h3 className="delivery-title">DELIVERY METHOD</h3>
          <div className="delivery-options">
            <label className={`delivery-option ${deliveryMethod === "Courier" ? "selected" : ""}`}>
              Courier Delivery 
              <input
                type="radio"
                name="deliveryMethod"
                value="Courier"
                checked={deliveryMethod === "Courier"}
                onChange={() => setDeliveryMethod("Courier")}
              />
              <span className="option-text" />
            </label>

            {(!shippingAddress.country || shippingAddress.country === "Nigeria") && (
              <label className={`delivery-option ${deliveryMethod === "CarPark" ? "selected" : ""}`}>
                Car Park Delivery (Pay at Pickup)
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="CarPark"
                  checked={deliveryMethod === "CarPark"}
                  onChange={() => setDeliveryMethod("CarPark")}
                />
                <span className="option-text" />
              </label>
            )}

            {deliveryMethod === "CarPark" && (
              <p className="delivery-note">
                🚚 This option is only for customers in <strong>Nigeria</strong>.
                You’ll be contacted with the delivery cost. Payment is made directly at the park when collecting your package.
              </p>
            )}
          </div>

          <label>COUNTRY</label>
          <select name="country" value={shippingAddress.country} onChange={handleInputChange} required>
            <option value="" disabled>Select your country</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>

          {shippingAddress.country === "Nigeria" && (
            <>
              <label>STATE</label>
              <select name="state" value={shippingAddress.state} onChange={handleInputChange} required>
                <option value="" disabled>Select your state</option>
                {Object.keys(NIGERIAN_STATES).map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </>
          )}

          <label>ADDRESS</label>
          <input type="text" name="address" value={shippingAddress.address} onChange={handleInputChange} required />

          <label>CITY</label>
          <input type="text" name="city" value={shippingAddress.city} onChange={handleInputChange} required />

          <label>ZIP CODE</label>
          <input type="text" name="zip" value={shippingAddress.zip} onChange={handleInputChange} />

          <label>TELEPHONE</label>
          <input type="tel" name="telephone" value={shippingAddress.telephone} onChange={handleInputChange} required />

          <label>MEASUREMENT</label>
          <textarea
            name="measurement"
            value={shippingAddress.measurement}
            onChange={handleInputChange}
            placeholder="Please write down your measurements here (e.g., Bust, Waist, Hips, Length)."
            rows={5}
            cols={50}
          />
        </form>
      </div>

      <div className="checkout-items">
        <h2>YOUR ORDER</h2>

        {stockRefreshing && (
          <p className="checkout-stock-refreshing">Checking current availability...</p>
        )}

        {stockIssue && (
          <div className="checkout-stock-warning">
            {Number(stockIssue.available_quantity || 0) <= 0
              ? `${stockIssue.name} is out of stock.`
              : `Only ${stockIssue.available_quantity} unit${Number(stockIssue.available_quantity) === 1 ? " is" : "s are"} available for ${stockIssue.name}.`}
            <span>Please return to your cart and update it before paying.</span>
          </div>
        )}

        {cartItems.map((product) => (
          <div key={product.id} className="checkout-item">
            <p>{product.name}</p>
            <p>
              ₦{formatMoney(product.price)} × {product.quantity}
            </p>
            {Number(product.available_quantity) > 0 &&
              Number(product.available_quantity) <= 2 && (
                <p>Only {product.available_quantity} left</p>
              )}
          </div>
        ))}

        <div className="checkout-summary">
          <p>SUBTOTAL : ₦{formatMoney(cartTotal)}</p>
          <p>MEASUREMENT : {shippingAddress.measurement}</p>
          <p>SHIPPING ADDRESS :</p>
          <p>
            {shippingAddress.address} {shippingAddress.city}{" "}
            {shippingAddress.state} {shippingAddress.country}
          </p>

          {deliveryMethod === "Courier" && (
            <p>
              SHIPPING FEE : ₦{formatMoney(baseShippingFee)} ×{" "}
              {totalItemQuantity} = ₦{formatMoney(shippingFee)}
            </p>
          )}

          <h3>TOTAL : ₦{formatMoney(totalAmount)}</h3>
        </div>

        <PaystackButton
          email={shippingAddress.email}
          amount={amountToPay}
          publicKey={publicKey}
          text="Pay Now"
          className={`paystack-btn ${!canPay ? "disabled" : ""}`}
          onSuccess={handlePaymentSuccess}
          onClose={handlePaymentClose}
          disabled={!canPay}
          onClick={() => {
            if (!publicKey) {
              showFloatingMessage("Payment is not configured. Please contact Posher.");
              return false;
            }

            if (!validateForm()) {
              showFloatingMessage("⚠️ Please fill all required fields");
              return false;
            }

            if (stockIssue || !cartItems.length) {
              showFloatingMessage("Please update your cart before paying.");
              return false;
            }

            return true;
          }}
        />

        {paymentBlockReason && (
          <p className="checkout-payment-note">
            {paymentBlockReason}
          </p>
        )}
      </div>

      {floatingMessage && (
        <div className="floating-message">{floatingMessage}</div>
      )}
    </div>
  );
};

export default Checkout;